import React, { JSX, useEffect, useRef, useState } from 'react';
import Axios from 'axios';
import Icons from 'components/atoms/Icons';
import Constants from 'assets/utils/constant';
import {
  LocationFiltersType,
  ProviderFiltersType,
  ReplaceLocationFiltersType,
  SetActiveFilterPanelType,
} from 'components/types/FilterTypes';
import {
  BuildAddress,
  SetLatLng,
  NormalizedAddress,
  LatLngInput,
} from 'components/types/LocationFunctionTypes';
import { classNames } from 'assets/utils/helper';
import DistanceFilter from './DistanceFilter';

type LocationFilterProps = {
  selectedFilters: LocationFiltersType | ProviderFiltersType;
  removeLocationFilters: () => void;
  replaceLocationFilters: ReplaceLocationFiltersType;
  geolocate: (setAddress: Function) => void;
  buildAddress: BuildAddress;
  setActiveFilterPanel?: SetActiveFilterPanelType;
  setLatLng: SetLatLng;
  geolocating: boolean;
  replaceFilter: Function;
};

type LocationFilterState = {
  address: string;
  latitude: number | string;
  location: string;
  longitude: number | string;
  zip_code: string;
};

type AzureSuggestion = {
  id: string;
  label: string;
  address: NormalizedAddress;
};

const AZURE_MAPS_ENDPOINT = 'https://atlas.microsoft.com';

const defaultBtnTxt = 'City or Zip';

const getAzureMapsKey = () =>
  process.env.NEXT_PUBLIC_AZURE_MAPS_SUBSCRIPTION_KEY;

const LocationFilter = (props: LocationFilterProps): JSX.Element => {
  const initialState: LocationFilterState = {
    address: '',
    latitude: props?.selectedFilters?.latitude || '',
    location: props?.selectedFilters?.location || '',
    longitude: props?.selectedFilters?.longitude || '',
    zip_code: props?.selectedFilters?.zip_code || '',
  };

  const [locationFilterState, setLocationFilterState] = useState({
    ...initialState,
  });

  const {
    removeLocationFilters,
    selectedFilters,
    geolocate,
    geolocating,
    replaceFilter,
  } = props;

  const [btnText, setBtnText] = useState(defaultBtnTxt);
  const [suggestions, setSuggestions] = useState<Array<AzureSuggestion>>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [address, setAddress] = useState<NormalizedAddress | null>(null);
  const [latLng, setLatLng] = useState<LatLngInput | null>(null);
  const [selected, setSelected] = useState(false);
  const [distance, setDistance] = useState(selectedFilters.distance);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [btnDisabled, setBtnDisabled] = useState(true);

  const fetchAzureSuggestions = async (query: string) => {
    const subscriptionKey = getAzureMapsKey();

    if (!subscriptionKey) {
      setSuggestions([]);
      return;
    }

    const normalize = (value?: string) => value?.toLowerCase().trim() || '';
    const queryText = normalize(query);
    const isZipSearch = /^\d+/.test(query.trim());

    try {
      setIsLoadingSuggestions(true);

      const response = await Axios.get(
        `${AZURE_MAPS_ENDPOINT}/search/fuzzy/json`,
        {
          params: {
            'api-version': '1.0',
            query,
            coordinates: `${Constants.mapCenter.lng},${Constants.mapCenter.lat}`,
            countrySet: 'US',
            limit: 15,
            typeahead: true,
            'subscription-key': subscriptionKey,
          },
          headers: {
            'Accept-Language': 'en-US',
          },
        }
      );

      const results = Array.isArray(response.data?.results)
        ? response.data.results
        : [];

      const normalizedSuggestions = results
        .filter((result: any) => {
          if (!isZipSearch) return true;

          const zip = result.address?.postalCode;

          return zip?.startsWith(query.trim());
        })
        .map((result: any) => {
          const address = result.address;

          const city = address?.municipality;
          const state = address?.countrySubdivision;
          const zip = address?.postalCode;

          const label = [city, state, zip].filter(Boolean).join(', ');

          return {
            id: `${label}-${result.type ?? ''}`,
            label,
            address: {
              label,
              city,
              state,
              zip,
            },
          };
        })
        .filter((suggestion: AzureSuggestion) => suggestion.label);

      const uniqueSuggestions = normalizedSuggestions.filter(
        (suggestion: AzureSuggestion, index: number, self: AzureSuggestion[]) =>
          index === self.findIndex(item => item.label === suggestion.label)
      );

      const prioritizedSuggestions = uniqueSuggestions.sort(
        (a: AzureSuggestion, b: AzureSuggestion) => {
          const aCity = normalize(a.address.city);
          const bCity = normalize(b.address.city);

          const aState = normalize(a.address.state);
          const bState = normalize(b.address.state);

          const aIsExactCityMatch = aCity === queryText;
          const bIsExactCityMatch = bCity === queryText;

          if (aIsExactCityMatch && !bIsExactCityMatch) return -1;
          if (!aIsExactCityMatch && bIsExactCityMatch) return 1;

          const aIsPreferredState =
            aState === 'il' ||
            aState === 'illinois' ||
            aState === 'mi' ||
            aState === 'michigan';

          const bIsPreferredState =
            bState === 'il' ||
            bState === 'illinois' ||
            bState === 'mi' ||
            bState === 'michigan';

          if (aIsPreferredState && !bIsPreferredState) return -1;
          if (!aIsPreferredState && bIsPreferredState) return 1;

          return 0;
        }
      );

      setSuggestions(prioritizedSuggestions.slice(0, 5));
      setShowSuggestions(true);
    } catch (error: any) {
      console.error('Azure fuzzy search error:', {
        message: error?.message,
        status: error?.response?.status,
        data: error?.response?.data,
      });

      setSuggestions([]);
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  const geocodeSelectedSuggestion = async (suggestion: AzureSuggestion) => {
    const subscriptionKey = getAzureMapsKey();

    if (!subscriptionKey) {
      return;
    }

    try {
      const response = await Axios.get(
        `${AZURE_MAPS_ENDPOINT}/search/address/json`,
        {
          params: {
            'api-version': '1.0',
            query: [
              suggestion.address.city,
              suggestion.address.state,
              suggestion.address.zip,
            ]
              .filter(Boolean)
              .join(', '),
            countrySet: 'US',
            limit: 1,
            'subscription-key': subscriptionKey,
          },
          headers: {
            'Accept-Language': 'en-US',
          },
        }
      );

      const result = response.data?.results?.[0];

      if (!result?.position?.lat || !result?.position?.lon) {
        return;
      }

      const latLng: LatLngInput = {
        lat: result.position.lat,
        lng: result.position.lon,
      };

      const address: NormalizedAddress = {
        label: suggestion.label,
        city: suggestion.address.city,
        state: suggestion.address.state,
        zip: suggestion.address.zip,
      };

      setAddress(address);
      setLatLng(latLng);
    } catch (error) {
      setSuggestions([]);
    }
  };

  const handleChange = (address: string) => {
    setSelected(false);
    setLocationFilterState(prevFields => ({
      ...prevFields,
      address,
    }));
  };

  const handleSelect = async (suggestion: AzureSuggestion) => {
    setSelected(true);
    setLocationFilterState(prevFields => ({
      ...prevFields,
      address: suggestion.label,
    }));

    const input = document.querySelector(
      '.location-search-input'
    ) as HTMLElement;
    input.blur();

    setShowSuggestions(false);
    await geocodeSelectedSuggestion(suggestion);
  };

  const handleArrowDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowDown') {
      (
        document.querySelector(`[data-suggestion-index="0"]`) as HTMLElement
      ).focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement>,
    index: number,
    suggestion: AzureSuggestion
  ) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      handleSelect(suggestion);
    }
    if (e.key === 'ArrowDown') {
      const next = document.querySelector(
        `[data-suggestion-index="${index + 1}"]`
      ) as HTMLElement;

      next?.focus();
    }

    if (e.key === 'ArrowUp') {
      if (e.currentTarget.getAttribute('data-suggestion-index') === '0') {
        (
          document.querySelector('.location-search-input') as HTMLElement
        )?.focus();
      } else {
        const prev = document.querySelector(
          `[data-suggestion-index="${index - 1}"]`
        ) as HTMLElement;
        prev?.focus();
      }
    }
  };

  const handleApply = () => {
    if (distance !== selectedFilters.distance) {
      replaceFilter('distance', distance);
    }
    setMenuOpen(false);

    address && latLng && props.buildAddress(address, latLng);
    setBtnText(`${distance} miles from ` + selectedFilters.location);
  };

  const handleClose = () => {
    setLocationFilterState(prevFields => ({
      ...prevFields,
      address: '',
    }));
    setSuggestions([]);
    removeLocationFilters();
    setAddress(null);
    setLatLng(null);
    setSelected(false);
    setBtnText(defaultBtnTxt);
    setBtnDisabled(true);
  };

  useEffect(() => {
    if (!selected) {
      const address = locationFilterState.address?.trim();

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      if (!address || address.length <= 2) {
        setSuggestions([]);
        return;
      }

      debounceRef.current = setTimeout(() => {
        fetchAzureSuggestions(address);
      }, 300);
    } else {
      setBtnDisabled(false);
    }
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [locationFilterState.address]);

  useEffect(() => {
    if (distance !== selectedFilters.distance) {
      setDistance(selectedFilters.distance);
    }
    setLocationFilterState(prevFields => ({
      ...prevFields,
      address:
        typeof selectedFilters.location === 'string'
          ? selectedFilters.location
          : '',
      distance: selectedFilters.distance,
    }));
    if (selectedFilters.location) {
      setBtnDisabled(false);
      setSelected(true);
      setMenuOpen(false);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      setBtnText(
        `${selectedFilters.distance} miles from ` + selectedFilters.location
      );
    } else {
      setBtnText(defaultBtnTxt);
    }
  }, [selectedFilters.location]);

  useEffect(() => {
    menuOpen &&
      !selectedFilters.location &&
      (document.querySelector('.location-search-input') as HTMLElement).focus();
  }, [menuOpen]);

  useEffect(() => {
    const handleClick = (e: Event) => {
      const targ = e.target as HTMLElement;
      if (
        !targ.closest('.location-filters-container') &&
        !targ?.classList?.contains('location-dropdown') &&
        !targ.closest('.location-dropdown')
      ) {
        setMenuOpen(false);
      }
    };
    window.addEventListener('click', handleClick);

    return () => {
      window.removeEventListener('click', handleClick);
    };
  }, []);

  return (
    <div className="location-filter">
      <button
        className={classNames(
          `location-dropdown osf-button-label filter-search-dropdown`,
          menuOpen ? 'open' : ''
        )}
        onClick={() => {
          setMenuOpen(!menuOpen);
        }}
      >
        <Icons
          id="location-external"
          className="location-external"
          aria-hidden="true"
        ></Icons>
        <span>{btnText}</span>
        {selectedFilters.location ? (
          <div
            className="search-bar-icon"
            onClick={event => {
              event.stopPropagation();
              handleClose();
            }}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.stopPropagation();
                handleClose();
              }
            }}
            tabIndex={0}
          >
            <Icons id="close" className="icon icon-close" aria-hidden="true" />
          </div>
        ) : (
          <Icons
            id="pld-chevron-down"
            className={'chevron-down'}
            aria-hidden="true"
          />
        )}
      </button>
      <div
        className={classNames(
          `location-filters-container`,
          menuOpen ? 'open' : ''
        )}
        hidden={menuOpen ? false : true}
      >
        <p className="location-label">Location</p>
        <div className="filter-btn-container">
          <div className="react-autosuggest__container input-icon-container">
            <input
              placeholder="Set city or ZIP"
              className="location-search-input input-text-box"
              disabled={false}
              type="text"
              value={locationFilterState.address || ''}
              onChange={event => handleChange(event.target.value)}
              onKeyDown={e => handleArrowDown(e)}
              onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
            />
            {locationFilterState?.address?.length > 2 &&
              suggestions.length > 0 && (
                <div
                  className={classNames(
                    'react-autosuggest__suggestions-container',
                    showSuggestions ? 'showing-suggestions' : ''
                  )}
                >
                  {suggestions.map((suggestion, i) => (
                    <div
                      className="typeahead-suggestion"
                      key={suggestion.id}
                      onMouseDown={event => {
                        event.preventDefault();
                        event.stopPropagation();
                        handleSelect(suggestion);
                      }}
                      onKeyDown={event => {
                        handleKeyDown(event, i, suggestion);
                      }}
                      tabIndex={0}
                      data-suggestion-index={i}
                    >
                      <span className="suggestion-name">
                        {suggestion.label}
                      </span>
                    </div>
                  ))}
                </div>
              )}

            {locationFilterState?.address?.length > 2 &&
              !isLoadingSuggestions &&
              suggestions.length === 0 && (
                <div className="react-autosuggest__suggestions-container">
                  <div className="typeahead-suggestion">
                    <span className="suggestion-name">No locations found</span>
                  </div>
                </div>
              )}

            {(selectedFilters.location || address) && (
              <div
                className="close-button"
                onClick={event => {
                  event.stopPropagation();
                  handleClose();
                }}
                onKeyDown={event => {
                  if (event.key === 'Enter') {
                    event.stopPropagation();
                    handleClose();
                  }
                }}
                tabIndex={0}
              >
                <Icons
                  id="close"
                  className="icon icon-close"
                  aria-hidden="true"
                />
              </div>
            )}
          </div>
        </div>
        {!selectedFilters.location ? (
          <button
            className={'use-location-btn' + (geolocating ? ' geolocating' : '')}
            onClick={event => {
              event.stopPropagation();

              geolocate(setAddress);

              setLocationFilterState(prevFields => ({
                ...prevFields,
              }));
            }}
          >
            <Icons id="location-arrow" aria-hidden="true"></Icons>
            {geolocating ? 'Getting location' : 'Use my location'}
          </button>
        ) : null}
        <DistanceFilter
          setDistance={setDistance}
          distance={distance}
          disabled={!selected}
        />
        <button
          className={classNames('btn-secondary no-icon btn-fw')}
          disabled={btnDisabled}
          onClick={() => handleApply()}
        >
          Apply
        </button>
      </div>
    </div>
  );
};

export default LocationFilter;
